import {useEffect, useReducer} from 'react';
import PropTypes from 'prop-types';
import {I18n} from 'react-redux-i18n';
import {useSelector} from 'react-redux';

import {isEmpty} from 'front-onceforall-core/dist/utils/strings';
import {useDebounce} from 'front-onceforall-core/dist/utils/customHooks';

import identificationNumberService from '../../services/identificationNumber/IdentificationNumberService';
import {getOrElse} from '../../utils/translation';
import {captureError} from '../../utils/log';
import OrganisationInvitationView from './OrganisationInvitationView';
import organisationService from '../../services/organisation/OrganisationService';

import ExternalInvitation from './externalInvitation';

const reducer = (current, payload) => ({
  ...current,
  ...payload
});

let aborter = new AbortController();
const pageSize = 10;

function OrganisationInvitationContainer(props) {
  const organisation = useSelector(globalState => globalState.organisation);
  const [state, setState] = useReducer(reducer, {
    country: organisation.country,
    search: '',
    postCode: '',
    loading: false,
    organisations: [],
    errorText: null,
    idNumbersLabel: '',
    isExternal: false,
    defaultExternalOrganisation: null,
    page: 0,
    totalCount: 0
  });
  const debouncedSearchValue = useDebounce(state.search);
  const debouncedPostCodeValue = useDebounce(state.postCode);

  function getIdentificationNumberLabelsForCountry(country) {
    identificationNumberService.findAllByCountry(country)
      .then(allIdNumbers => {
        const cleanedIdNumbers = allIdNumbers
        // Sorted on id to eventually get REGISTRATION_NUMBER first (id order is not assured but we don't really care)
          .sort((curr, prev) => prev.id - curr.id)
        // Reducing on format and keeping first for the case when the registration number is, in fact, the VAT number
          .reduce((idNumbersByFormat, idNumber) => ({...idNumbersByFormat, [idNumber.format]: idNumber}), Object.create(null));

        const idNumbersLabel = Object.values(cleanedIdNumbers)
          // Gets shortName for given country and identification number (esp. for Germany) or name if it doesn't exist
          .map(idNumber => getOrElse(
              `identificationNumber.${country}.${idNumber.code}.shortName`,
              `identificationNumber.${country}.${idNumber.code}.name`
          ));
        setState({idNumbersLabel: [I18n.t('organisation.fullName'), ...idNumbersLabel].join(', ')});
      })
      .catch(error => {
        captureError('Error while getting id numbers for specific country', error);
        // If the backend call fails or doesn't answer, we should have at least 'Organisation name' as label
        setState({idNumbersLabel: I18n.t('organisation.fullName')});
      });
  }

  function handleSearch(search) {
    setState({search, errorText: null});
  }

  function handleChangeCountry(event) {
    const country = event.target.value;
    setState({country, search: '', postCode: '', errorText: null});
    getIdentificationNumberLabelsForCountry(country);
  }

  function handleOrganisationSelect(invitedOrganisation) {
    if (invitedOrganisation.organisationId === organisation.organisationId) {
      setState({errorText: 'organisation.search.self'});
      return false;
    }

    setState({loading: true});

    return props.onSelectInternalInvitation(invitedOrganisation)
      .then(() => {
        if (props.externalInvitationOnNoDepositor && !invitedOrganisation.depositor) {
          setState({
            defaultExternalOrganisation: invitedOrganisation,
            isExternal: true
          });
        }

        return Promise.resolve();
      })
      .catch(error => setState({errorText: error.message, loading: false}));
  }

  function handleInternalInvitationFromExternalInvitationForm(invitedOrganisation) {
    setState({
      isExternal: false,
      country: invitedOrganisation.country,
      search: invitedOrganisation.registrationNumber,
      postCode: '',
      errorText: null
    });
    handleOrganisationSelect(invitedOrganisation);
  }

  function searchOrganisations(page, signal) {
    const searchParams = {
      query: state.search,
      country: state.country,
      postCode: state.postCode,
      enabled: true
    };

    organisationService.search(searchParams, page, pageSize, signal)
      .then(({organisations, totalCount}) => {
        const mergedOrganisations = page > 0 ? [...state.organisations, ...organisations] : [...organisations];
        setState({
          organisations: mergedOrganisations,
          totalCount,
          loading: false
        });
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          captureError('Error while searching organisation for internal invitation', error);
        }
      });
  }

  function handleLoadMoreResults() {
    if (state.organisations.length < state.totalCount) {
      setState({page: state.page + 1});
      searchOrganisations(state.page + 1, null);
    }
  }

  function handleCancelExternalInvitation() {
    if (props.reinvitedOrganisation) {
      props.onCancel();
    } else {
      setState({isExternal: false});
    }
  }

  useEffect(() => {
    if (props.reinvitedOrganisation) {
      setState({
        defaultExternalOrganisation: props.reinvitedOrganisation,
        isExternal: true
      });
    } else {
      getIdentificationNumberLabelsForCountry(state.country);
    }
  }, []);

  useEffect(() => {
    setState({page: 0});

    if (isEmpty(debouncedSearchValue) && isEmpty(debouncedPostCodeValue)) {
      setState({loading: false, organisations: []});
      aborter.abort();
      return;
    }

    aborter = new AbortController();

    setState({loading: true});

    searchOrganisations(0, aborter.signal);
  }, [debouncedSearchValue, debouncedPostCodeValue]);

  return state.isExternal ?
    <ExternalInvitation
      title={props.title}
      onCancel={handleCancelExternalInvitation}
      onValidate={props.onExternalInvitation}
      onInviteInternalOrganisation={handleInternalInvitationFromExternalInvitationForm}
      translateInvitationMessage={props.onTranslateInvitationMessage}
      defaultValue={state.search}
      defaultCountry={state.country}
      invitingOrganisationCountry={organisation.country}
      defaultOrganisation={state.defaultExternalOrganisation}
      disableOrganisationFields={Boolean(props.reinvitedOrganisation)}
    /> :
    <OrganisationInvitationView
      organisations={state.organisations}
      search={state.search}
      onSearch={handleSearch}
      postCode={state.postCode}
      onChangePostCode={event => setState({postCode: event.target.value})}
      country={state.country}
      onChangeCountry={handleChangeCountry}
      loading={state.loading}
      title={props.title}
      notice={props.notice}
      onCancel={props.onCancel}
      onSelect={handleOrganisationSelect}
      errorText={state.errorText}
      onInviteExternalOrganisation={() => setState({isExternal: true})}
      idNumbersLabel={state.idNumbersLabel}
      onLoadMoreResults={handleLoadMoreResults}
    />;
}

OrganisationInvitationContainer.defaultProps = {
  externalInvitationOnNoDepositor: false
};

OrganisationInvitationContainer.propTypes = {
  externalInvitationOnNoDepositor: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  notice: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onCancel: PropTypes.func,
  onSelectInternalInvitation: PropTypes.func.isRequired,
  onExternalInvitation: PropTypes.func.isRequired,
  onTranslateInvitationMessage: PropTypes.func,
  reinvitedOrganisation: PropTypes.object
};

export default OrganisationInvitationContainer;
